import { useMemo, useState } from 'react'
import { Field, Select, ResultStat } from '@/components/calc/ui'

type Scale = 'HO' | 'N' | 'O'

const GAUGE: Record<Scale, { mm: number; label: string }> = {
  HO: { mm: 16.5, label: 'HO Scale (1:87)' },
  N: { mm: 9.0, label: 'N Scale (1:160)' },
  O: { mm: 32.0, label: 'O Scale (1:48)' },
}

const FROGS = [4, 6, 8, 10]

function fmtMm(mm: number) {
  return `${mm.toFixed(1)} mm`
}
function fmtIn(mm: number) {
  return `${(mm / 25.4).toFixed(3)} in`
}

export default function RailroadTurnout() {
  const [scale, setScale] = useState<Scale>('HO')
  const [frog, setFrog] = useState<number>(6)

  const r = useMemo(() => {
    const G = GAUGE[scale].mm
    const lead = 2 * G * frog
    const radius = 2 * G * frog * frog
    const angle = 2 * Math.atan(1 / (2 * frog)) * (180 / Math.PI)
    return { G, lead, radius, angle }
  }, [scale, frog])

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <div className="space-y-4">
        <Field label="Rail Scale">
          <Select<Scale>
            value={scale}
            onChange={setScale}
            options={(Object.keys(GAUGE) as Scale[]).map((k) => ({ label: GAUGE[k].label, value: k }))}
          />
        </Field>
        <Field label="Frog Number" hint="turnout angle ratio">
          <Select<number>
            value={frog}
            onChange={setFrog}
            options={FROGS.map((f) => ({ label: `#${f} turnout`, value: f }))}
          />
        </Field>

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 font-mono text-xs leading-relaxed text-slate-500">
          <div>L = 2 &times; G &times; N</div>
          <div>R = 2 &times; G &times; N&sup2;</div>
          <div>&theta; = 2 &times; arctan(1 / 2N)</div>
          <div className="mt-2 text-slate-400">G (gauge) = {fmtMm(r.G)} for {scale} scale</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 content-start">
        <ResultStat label="Gauge Width (G)" value={fmtMm(r.G)} sub={fmtIn(r.G)} />
        <ResultStat label="Frog Angle" value={`${r.angle.toFixed(2)}°`} sub="exact geometry" accent="text-emerald-600" />
        <ResultStat label="Turnout Lead (L)" value={fmtMm(r.lead)} sub={fmtIn(r.lead)} />
        <ResultStat label="Curve Radius (R)" value={fmtMm(r.radius)} sub={fmtIn(r.radius)} />
      </div>
    </div>
  )
}
